import { type FunctionComponent, useMemo } from "react";
import classnames from "classnames";

import { type PNLAndWhatIfProps, PNLAndWhatIfSize } from "./PNLAndWhatIf.types";

import styles from "./PNLAndWhatIf.web.css";

const Value: FunctionComponent<{
  value: PNLAndWhatIfProps["pnl"];
  rawValue: PNLAndWhatIfProps["rawPnl"];
  valueTextStyle?: string;
  testID: string;
  disabled?: boolean;
  boosted?: boolean;
  shouldUseNeutralColor?: boolean;
}> = ({ value, rawValue, valueTextStyle, testID, disabled, boosted, shouldUseNeutralColor = true }) => {
  const valueClassNames = useMemo(
    () =>
      classnames(
        styles.value,
        {
          [styles.profit]:
            (shouldUseNeutralColor && rawValue !== undefined && rawValue > 0) ||
            (!shouldUseNeutralColor && rawValue !== undefined && rawValue >= 0),
          [styles.neutral]: shouldUseNeutralColor && rawValue !== undefined && rawValue === 0,
          [styles.loss]: rawValue !== undefined && rawValue < 0,
          [styles.disabled]: disabled,
          [styles.boosted]: boosted,
        },
        valueTextStyle,
      ),
    [rawValue, valueTextStyle, disabled, boosted, shouldUseNeutralColor],
  );

  return (
    <span className={valueClassNames} id={testID}>
      {value}
    </span>
  );
};

/**
 * Displays profit/loss (PNL) and "what-if" values with color-coded indicators.
 *
 * The PNLAndWhatIf component shows financial information with visual indicators for profit (green),
 * loss (red), or neutral (default) states. It supports previous values for comparison, multiple sizes,
 * disabled state, and an agnostic variant for colored backgrounds. Values can be boosted with
 * special styling.
 */
export const PNLAndWhatIf: FunctionComponent<PNLAndWhatIfProps> = ({
  pnl,
  rawPnl,
  pnlLose,
  rawPnlLose,
  previousPnl,
  whatIf,
  rawWhatIf,
  whatIfLose,
  rawWhatIfLose,
  size = PNLAndWhatIfSize.MEDIUM,
  disabled = false,
  agnostic = false,
  boosted = false,
  pnlAndWhatIfEachWay,
}) => {
  const isEachWay = pnlAndWhatIfEachWay && pnlAndWhatIfEachWay.length > 0;

  const { separatorStyles, previousValueStyle, containerClassNames } = useMemo(
    () => ({
      previousValueStyle: classnames(styles.previousValue, {
        [styles.disabled]: disabled,
        [styles.previousValueSmall]: size === PNLAndWhatIfSize.SMALL,
        [styles.previousValueMedium]: size === PNLAndWhatIfSize.MEDIUM,
        [styles.previousValueLarge]: size === PNLAndWhatIfSize.LARGE,
      }),
      separatorStyles: classnames(styles.separator, {
        [styles.separatorDisabled]: disabled,
        [styles.separatorSmall]: size === PNLAndWhatIfSize.SMALL,
        [styles.separatorMedium]: size === PNLAndWhatIfSize.MEDIUM,
        [styles.separatorLarge]: size === PNLAndWhatIfSize.LARGE,
      }),
      containerClassNames: classnames(styles.container, {
        [styles.eachWayContainer]: isEachWay,
        [styles.agnostic]: agnostic,
        [styles.disabled]: disabled,
        [styles.small]: size === PNLAndWhatIfSize.SMALL,
        [styles.medium]: size === PNLAndWhatIfSize.MEDIUM,
        [styles.large]: size === PNLAndWhatIfSize.LARGE,
      }),
    }),
    [agnostic, disabled, size, isEachWay],
  );

  const getMarkerClass = (raw: number, useNeutral = true) =>
    classnames(separatorStyles, {
      [styles.profit]: (useNeutral && raw > 0) || (!useNeutral && raw >= 0),
      [styles.neutral]: useNeutral && raw === 0,
      [styles.loss]: raw < 0,
    });

  return isEachWay ? (
    <div className={containerClassNames}>
      {pnlAndWhatIfEachWay.map((row, index) => {
        const { hasMarker, winRaw, win, placed, unplaced, placedRaw, unplacedRaw } = row;
        return (
          <p className={styles.eachWayPnlRow} key={`pnlEW-row-${index}`}>
            {hasMarker && <span className={getMarkerClass(winRaw, false)}>»</span>}
            <Value value={win} rawValue={winRaw} testID={`pnlEW-${index}-win`} shouldUseNeutralColor={false} />
            <Value value={placed} rawValue={placedRaw} testID={`pnlEW-${index}-placed`} shouldUseNeutralColor={true} />
            <Value
              value={unplaced}
              rawValue={unplacedRaw}
              testID={`pnlEW-${index}-unplaced`}
              shouldUseNeutralColor={false}
            />
          </p>
        );
      })}
    </div>
  ) : (
    <p className={containerClassNames}>
      {!!previousPnl && <span className={previousValueStyle}>{previousPnl}</span>}
      {!!pnl && (
        <Value value={pnl} rawValue={rawPnl} testID="pnl" disabled={disabled} boosted={boosted && !!previousPnl} />
      )}
      {!!pnlLose && <Value value={pnlLose} rawValue={rawPnlLose} testID="pnl-lose" disabled={disabled} />}
      {!!pnl && !!whatIf && <span className={separatorStyles}>»</span>}
      {!!whatIf && (
        <Value
          value={whatIf}
          rawValue={rawWhatIf}
          valueTextStyle={styles.whatIf}
          testID="what-if"
          disabled={disabled}
        />
      )}
      {!!whatIfLose && (
        <Value
          value={whatIfLose}
          rawValue={rawWhatIfLose}
          valueTextStyle={styles.whatIf}
          testID="what-if-lose"
          disabled={disabled}
        />
      )}
    </p>
  );
};
